import { useState } from "react";
import { Smartphone, Send, Clock } from "lucide-react";
import { Button } from "@cieda/djayma-ui";
import { toast } from "sonner";
import AdminLayout from "@/components/admin/AdminLayout";
import { EmptyState } from "@/components/admin/shared";
import {
  useSMSCampaigns,
  useSMSTemplates,
  useCreateSMSCampaign,
} from "@/hooks/admin/useCRM";
import type { SegmentKey, SMSCampaignPayload, SMSCampaignRecord } from "@/lib/admin-api/crm";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SMS_CHAR_LIMIT = 160;

const SEGMENT_OPTIONS: Array<{ key: SegmentKey; label: string }> = [
  { key: "vip", label: "VIP" },
  { key: "at_risk", label: "À risque" },
  { key: "dormant", label: "Dormant" },
  { key: "new", label: "Nouveau" },
  { key: "high_frequency", label: "Haute fréquence" },
  { key: "cod_only", label: "COD uniquement" },
  { key: "mobile_money_only", label: "Mobile money uniquement" },
];

const CAMPAIGN_STATUS_LABELS: Record<SMSCampaignRecord["status"], string> = {
  draft: "Brouillon",
  scheduled: "Programmé",
  sending: "En cours",
  sent: "Envoyé",
  failed: "Échoué",
};

const CAMPAIGN_STATUS_STYLES: Record<SMSCampaignRecord["status"], string> = {
  draft: "bg-stone-100 text-stone-600",
  scheduled: "bg-blue-50 text-blue-700",
  sending: "bg-amber-50 text-amber-700",
  sent: "bg-emerald-50 text-emerald-700",
  failed: "bg-red-50 text-red-700",
};

type Tab = "new" | "history";

// ---------------------------------------------------------------------------
// Tab nav
// ---------------------------------------------------------------------------

function TabNav({ active, onChange }: { active: Tab; onChange: (t: Tab) => void }) {
  return (
    <div className="flex gap-1 border-b border-stone-200">
      {([
        { key: "new" as Tab, label: "Nouvelle campagne" },
        { key: "history" as Tab, label: "Historique" },
      ] as const).map((tab) => (
        <button
          key={tab.key}
          onClick={() => onChange(tab.key)}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
            active === tab.key
              ? "border-amber-500 text-amber-700"
              : "border-transparent text-stone-500 hover:text-stone-800"
          }`}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// New campaign form
// ---------------------------------------------------------------------------

function NewCampaignTab() {
  const { data: templatesData, isLoading: loadingTemplates } = useSMSTemplates();
  const createCampaign = useCreateSMSCampaign();

  const [segmentKey, setSegmentKey] = useState<SegmentKey | "">("");
  const [templateId, setTemplateId] = useState("");
  const [messageOverride, setMessageOverride] = useState("");
  const [scheduledAt, setScheduledAt] = useState("");

  const templates = templatesData?.templates ?? [];

  const charCount = messageOverride.length;
  const smsCount = Math.ceil(charCount / SMS_CHAR_LIMIT) || 1;
  const isOverLimit = charCount > SMS_CHAR_LIMIT * 3;

  const handleSubmit = () => {
    if (!segmentKey) { toast.error("Veuillez sélectionner un segment."); return; }
    if (!templateId && !messageOverride.trim()) {
      toast.error("Veuillez sélectionner un modèle ou saisir un message.");
      return;
    }
    if (isOverLimit) { toast.error("Le message est trop long."); return; }

    const payload: SMSCampaignPayload = {
      segment_key: segmentKey as SegmentKey,
      template_id: templateId,
      ...(messageOverride.trim() && { message_override: messageOverride }),
      scheduled_at: scheduledAt || null,
    };

    createCampaign.mutate(payload, {
      onSuccess: () => {
        toast.success(
          scheduledAt ? "Campagne SMS programmée avec succès." : "Campagne SMS lancée.",
        );
        setSegmentKey("");
        setTemplateId("");
        setMessageOverride("");
        setScheduledAt("");
      },
      onError: () => toast.error("Erreur lors de la création de la campagne."),
    });
  };

  return (
    <div className="max-w-xl space-y-5">
      <div className="rounded-xl border border-stone-200 bg-white p-5 space-y-5">
        {/* Segment */}
        <div className="space-y-1">
          <label className="text-sm font-medium text-stone-700">
            Segment cible <span className="text-red-500">*</span>
          </label>
          <select
            value={segmentKey}
            onChange={(e) => setSegmentKey(e.target.value as SegmentKey | "")}
            className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-300"
          >
            <option value="">Sélectionner un segment...</option>
            {SEGMENT_OPTIONS.map((s) => (
              <option key={s.key} value={s.key}>{s.label}</option>
            ))}
          </select>
        </div>

        {/* Template */}
        <div className="space-y-1">
          <label className="text-sm font-medium text-stone-700">Modèle de message</label>
          {loadingTemplates ? (
            <div className="h-10 rounded-lg bg-stone-100 animate-pulse" />
          ) : (
            <select
              value={templateId}
              onChange={(e) => {
                setTemplateId(e.target.value);
                const tpl = templates.find((t) => t.id === e.target.value);
                if (tpl) setMessageOverride(tpl.body);
              }}
              className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-300"
            >
              <option value="">Aucun modèle (saisir manuellement)</option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          )}
        </div>

        {/* Message */}
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-stone-700">
              Message <span className="text-red-500">*</span>
            </label>
            <span className={`text-xs font-medium tabular-nums ${isOverLimit ? "text-red-600" : "text-stone-400"}`}>
              {charCount}/{SMS_CHAR_LIMIT} — {smsCount} SMS
            </span>
          </div>
          <textarea
            value={messageOverride}
            onChange={(e) => setMessageOverride(e.target.value)}
            rows={4}
            placeholder="Bonjour {prénom}, votre commande {ref_commande} est disponible..."
            className={`w-full resize-none rounded-lg border px-3 py-2 text-sm placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-300 ${
              isOverLimit ? "border-red-300" : "border-stone-200"
            }`}
          />
          <p className="text-xs text-stone-400">
            Champs : {"{prénom}"}, {"{ref_commande}"} — 160 caractères = 1 SMS
          </p>
        </div>

        {/* Schedule */}
        <div className="space-y-1">
          <label className="text-sm font-medium text-stone-700 flex items-center gap-1.5">
            <Clock className="h-4 w-4" />
            Programmer l'envoi (laisser vide pour envoyer maintenant)
          </label>
          <input
            type="datetime-local"
            value={scheduledAt}
            onChange={(e) => setScheduledAt(e.target.value)}
            className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
          />
        </div>

        <Button
          onClick={handleSubmit}
          disabled={createCampaign.isPending}
          className="w-full gap-2"
        >
          <Send className="h-4 w-4" />
          {createCampaign.isPending
            ? "Création en cours..."
            : scheduledAt
              ? "Programmer la campagne"
              : "Lancer la campagne"}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// History tab
// ---------------------------------------------------------------------------

function HistoryTab() {
  const { data, isLoading } = useSMSCampaigns();
  const campaigns = data?.campaigns ?? [];

  if (isLoading) {
    return (
      <div className="space-y-3 animate-pulse">
        {[...Array(5)].map((_, i) => <div key={i} className="h-12 rounded bg-stone-100" />)}
      </div>
    );
  }

  if (campaigns.length === 0) {
    return (
      <EmptyState
        icon={<Smartphone className="h-6 w-6" />}
        title="Aucune campagne SMS"
        description="Créez votre première campagne depuis l'onglet Nouvelle campagne."
      />
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-stone-200 bg-white">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-stone-100 bg-stone-50">
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Segment</th>
              <th className="hidden md:table-cell px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Aperçu</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Statut</th>
              <th className="hidden sm:table-cell px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-stone-500">Destinataires</th>
              <th className="hidden lg:table-cell px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-stone-500">Livrés</th>
              <th className="hidden sm:table-cell px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Date</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {campaigns.map((c) => (
              <tr key={c.id} className="hover:bg-stone-50 transition-colors">
                <td className="px-4 py-3">
                  <span className="inline-flex items-center rounded-full bg-stone-100 px-2.5 py-0.5 text-xs font-medium text-stone-700">
                    {c.segment_name}
                  </span>
                </td>
                <td className="hidden md:table-cell px-4 py-3 max-w-xs truncate text-stone-600">
                  {c.message_preview}
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${CAMPAIGN_STATUS_STYLES[c.status]}`}>
                    {CAMPAIGN_STATUS_LABELS[c.status]}
                  </span>
                </td>
                <td className="hidden sm:table-cell px-4 py-3 text-right tabular-nums text-stone-800">
                  {c.recipient_count.toLocaleString("fr-SN")}
                </td>
                <td className="hidden lg:table-cell px-4 py-3 text-right tabular-nums text-stone-600">
                  {c.delivered_count.toLocaleString("fr-SN")}
                </td>
                <td className="hidden sm:table-cell px-4 py-3 text-stone-500">
                  {c.scheduled_at
                    ? new Date(c.scheduled_at).toLocaleDateString("fr-SN")
                    : new Date(c.created_at).toLocaleDateString("fr-SN")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function SMS() {
  const [tab, setTab] = useState<Tab>("new");

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100 text-blue-700">
            <Smartphone className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-xl font-semibold text-stone-900">
              Campagnes SMS
            </h1>
            <p className="text-sm text-stone-500">
              Envoi ciblé de messages texte par segment client
            </p>
          </div>
        </div>

        <TabNav active={tab} onChange={setTab} />

        {tab === "new" && <NewCampaignTab />}
        {tab === "history" && <HistoryTab />}
      </div>
    </AdminLayout>
  );
}
